// ✅ โค้ดเต็มของ GptSuggest.jsx พร้อมคลิกขยายรูปภาพ
import { useState, useEffect, useRef, useMemo, useImperativeHandle, forwardRef } from 'react';
import { ArrowUp, Copy, Plus, Paperclip, Sparkles } from 'lucide-react';
import { initializeApp } from 'firebase/app';
import { getFirestore, doc, getDoc, setDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import refineryAssets from './Refinery-Asset/refinery-assets.json';

// ดึง URL ของรูป (รวม query เช่น ?alt=media, ?token=xxx)
const extractImageUrls = (text = '') => {
  const rx = /https?:\/\/[^\s"'<>]+?\.(?:png|jpg|jpeg|webp|gif)(?:\?[^\s"'<>]*)?/gi;
  const out = [];
  let m;
  while ((m = rx.exec(text)) !== null) out.push(m[0]); // ✅ เอาทั้ง match พร้อม query
  return Array.from(new Set(out)); // กันซ้ำ
};

// ลบ URL รูปออกจากข้อความ
// ลบหัวบล็อกภาพ + URL รูป + เศษบรรทัดไฟล์ (.jpg?alt=media ฯลฯ)
const stripImageUrlsFromText = (text = '') => {
  let t = String(text || '');

  // 1) ตัดหัวบล็อกภาพมาตรฐาน (TH/EN)
  t = t.replace(/^\s*ชุดภาพที่แนะนำให้ส่งให้ลูกค้า.*$/gmi, '');
  t = t.replace(/^\s*Additional image set.*$/gmi, '');

  // 2) ตัด URL ไฟล์รูปทั้งหมด
  t = t.replace(/https?:\/\/[^\s"'<>]+?\.(?:png|jpe?g|webp|gif)(?:\?[^\s"'<>]*)?/gmi, '');

  // 3) ตัดบรรทัดเศษไฟล์ เช่น ".jpg?alt=media" / "jpg?alt=media" / ".png?..."
  t = t.replace(/^\s*\.?jpe?g(?:\?[^\s]*)?\s*$/gmi, '');
  t = t.replace(/^\s*\.?png(?:\?[^\s]*)?\s*$/gmi, '');
  t = t.replace(/^\s*\.?webp(?:\?[^\s]*)?\s*$/gmi, '');

  // 4) เก็บช่องว่าง/คั่นบล็อกให้สวย
  t = t.replace(/(\n\s*){3,}/g, '\n\n').trim();

  return t;
};

function FitThumb({ url, onClick, frame = '4/5', width = 120 }) {
  // กำหนดกรอบมาตรฐาน 4:5 เพื่อให้ทุกภาพกว้างเท่ากัน สูงเท่ากัน
  // ถ้าอยากใช้ 1:1 ก็เปลี่ยน frame เป็น '1/1'
  const style = { aspectRatio: frame === '1/1' ? '1 / 1' : '4 / 5', width: `${width}px`, height: 'auto' };

  return (
    <div
      className="relative shrink-0 overflow-hidden rounded-md border border-gray-200 bg-white"
      style={style}
    >
      <img
        src={url}
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        draggable={false}
        onClick={() => onClick?.(url)}
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
        // สำคัญ: ใช้ object-contain เพื่อ “ย่อให้พอดีกรอบ” พร้อมเหลือขอบขาวเป็นกรอบ
        className="absolute inset-0 w-full h-full object-contain object-center cursor-pointer block"
        alt="review-thumb"
      />
    </div>
  );
}


// ล้างบรรทัดที่เป็น "<>" เปล่า ๆ เผื่อ cleanAliasFromText เคยแทนไว้
const stripAnglePlaceholders = (text = '') =>
  text.replace(/^\s*<>\s*$/gm, '').trim();


// GptSuggest.jsx — PATCH (drop-in)

// ✅ REPLACE the existing "resolveAllImageUrlsFromAlias" with this safe version
const resolveAllImageUrlsFromAlias = (content = '') => {
  const text = String(content || '');

  // --- 0) ตรวจว่าเป็น "บล็อกภาพ" เท่านั้นถึงจะ resolve จาก alias
  const isImageBlock =
    /^(ชุดภาพที่แนะนำ|Additional image set|Recommended English menu page)/im.test(text);
  if (!isImageBlock) return []; // กันเคสข้อความ EN ปกติที่มีคำว่า price แล้วโชว์รูป

  // --- 1) utility: ตรวจกำกับเป็นหัวบล็อก EN เมนู
  const isEnglishMenuHeader =
    /(English menu|menu_eng|Recommended English menu page)/i.test(text);

  // --- 2) ช่วยดึง token ที่เป็น "alias เดี่ยว" ต่อบรรทัด/บูลเล็ต เท่านั้น
  const pickStandaloneTokens = (raw) => {
    return String(raw || '')
      .split(/\n+/)
      .map((line) => line.trim())
      .map((line) => {
        // รองรับ bullet  -, •, +, 1. 2.
        const m = line.match(/^(?:[+\-•]|\d+\.)?\s*([a-z0-9._-]+)\s*$/i);
        return m ? String(m[1] || '').toLowerCase() : '';
      })
      .filter(Boolean);
  };

  // --- 3) map alias -> url เฉพาะชุดที่ต้องใช้
  const aliasMapFromCategory = (catKey) => {
    const arr = (refineryAssets?.[catKey] || []).filter(Boolean);
    const map = new Map();
    for (const it of arr) {
      const al = String(it.alias || '').toLowerCase();
      const url = String(it.url || '');
      if (al && url) map.set(al, url);
    }
    return map;
  };

  const tokens = pickStandaloneTokens(text);
  const urls = [];

  if (isEnglishMenuHeader) {
    // --- EN MODE: อนุญาตเฉพาะ menu_eng เท่านั้น และตัด cover + จำกัด 3 รูป
    const menuMap = aliasMapFromCategory('menu_eng');
    for (const tk of tokens) {
      if (tk === 'cover') continue;
      const url = menuMap.get(tk);
      if (url && !urls.includes(url)) {
        urls.push(url);
        if (urls.length >= 3) break;
      }
    }
    return urls;
  }

  // --- TH บล็อกภาพ (เดิม): อนุญาตทุกหมวดตาม alias เดี่ยว ๆ
  //     *NOTA*: ไม่แตะเมนู EN ที่นี่ (ให้ส่วนบนจัดการ)
  const allItems = Object.values(refineryAssets || {}).flat().filter(Boolean);
  const itemByAlias = new Map(
    allItems.map((it) => [String(it.alias || '').toLowerCase(), String(it.url || '')])
  );

  for (const tk of tokens) {
    const url = itemByAlias.get(tk);
    if (url && !urls.includes(url)) urls.push(url);
  }
  return urls;
};


// ⬆️ วาง helper นี้ไว้ด้านบนไฟล์ (ใกล้ๆ ฟังก์ชัน util อื่นๆ)
const stripImageSetHeaders = (text) => {
  return (text || "")
    // ตัดหัวบล็อกภาพ (EN/TH) ให้เหลือแต่เนื้อความ
    .replace(
      /^\s*(Additional image set for English menu\s*\(menu_eng\):)\s*$/gmi,
      ""
    )
    .replace(
      /^\s*(Recommended English menu page\(s\).*?)\s*$/gmi,
      ""
    )
    // เก็บ UI ให้สะอาด
    .replace(/^\s*<>\s*$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const cleanAliasFromText = (text = '') => {
  const lines = String(text || '').split('\n');

  // รวบรวม alias ทั้งหมด + ชื่อคีย์บริการจาก refineryAssets (ถ้าไม่มี ให้ fall back เป็นอาเรย์ว่าง)
  const aliases = [];
  const serviceKeys = refineryAssets ? Object.keys(refineryAssets) : [];

  if (refineryAssets) {
    Object.values(refineryAssets).forEach((category) => {
      // บางโปรเจ็กต์ category อาจเป็นอาเรย์ของ item
      (category || []).forEach((item) => {
        if (item && item.alias) aliases.push(String(item.alias));
      });
    });
  }

  const lowerAliases = new Set(aliases.map((a) => a.toLowerCase()));
  const lowerKeys = new Set(serviceKeys.map((k) => k.toLowerCase()));

  // ลบเฉพาะ “บรรทัดที่เป็น token เดี่ยวๆ” หรือ bullet ที่มี token เดียวเท่านั้น
  const isStandaloneTokenLine = (rawLine) => {
    const lc = String(rawLine || '').toLowerCase().trim();

    // กรณีเป็น token เดี่ยว ๆ ตรงตัว (เช่น "oligio", "cover")
    if (lowerKeys.has(lc) || lowerAliases.has(lc)) return true;

    // bullet ที่มีแค่ token เดียว (รองรับ -, •, +)
    const m = lc.match(/^\s*[+\-•]?\s*([a-z0-9._-]+)\s*$/i);
    if (m) {
      const tok = (m[1] || '').toLowerCase();
      if (lowerKeys.has(tok) || lowerAliases.has(tok)) return true;
    }

    // บรรทัดรูปแบบ "label —" ที่เหลือจากการลบ URL ออก (กันไม่ให้เหลือขีดยาวลอย ๆ)
    if (/—\s*$/i.test(lc)) return true;

    return false;
  };

  const cleanedLines = lines.filter((line) => {
    // ❌ ลบเฉพาะ "บรรทัดที่เป็น token เดี่ยว ๆ" ตามกฎด้านบน
    if (isStandaloneTokenLine(line)) return false;

    // ✅ อนุญาตให้มีชื่อบริการ/alias ปรากฏ “ภายในประโยค” ได้
    // เช่น "+ Oligio 300 Shots", "รีวิว [Oligio + Ultraformer III]" จะไม่โดนลบ
    return true;
  });

  return cleanedLines.join('\n').trim();
};

const firebaseConfig = {
  apiKey: "AIzaSyBALO-oNqvxmRhTSx9S54A5PIilTrxVOYM",
  authDomain: "refinery-desk-dashboard.firebaseapp.com",
  projectId: "refinery-desk-dashboard",
  storageBucket: "refinery-desk-dashboard.firebasestorage.app",
  messagingSenderId: "28035012838",
  appId: "1:28035012838:web:eb80f171a10371cdbec327"
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const storage = getStorage(app);

const GptSuggest = forwardRef(({ selectedUserId, chatMessagesFromCustomer, selectedCustomer }, ref) => {
  const [messagesMap, setMessagesMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');


  const handleSendImageToLine = async (imageUrl) => {
    if (!selectedUserId || !imageUrl) return;

    try {
      const res = await fetch('https://therefineryclinic.herokuapp.com/sendToLineImage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: selectedUserId, imageUrl }),
      });

      if (!res.ok) throw new Error('ส่งไม่สำเร็จ');

      // บันทึกข้อความเข้า Firestore
      const latestCustomerInfo = selectedCustomer || {};

      await addDoc(collection(db, 'messages'), {
        from: 'me',
        role: 'me',
        userId: selectedUserId,
        type: 'image',
        imageUrl,
        createdAt: serverTimestamp(),
        displayName: latestCustomerInfo.displayName || 'Customer',
        pictureUrl: latestCustomerInfo.pictureUrl || '',
      });
      setPreviewImageUrl(null);
      setSuccessMessage('ส่งภาพสำเร็จแล้ว');
      setTimeout(() => setSuccessMessage(''), 1800);
    } catch (err) {
      console.error(err);
      alert('❌ ไม่สามารถส่งภาพได้');
    }
  };

  const containerRef = useRef(null);
  const contentRef = useRef(null);

  const templateOptions = ['This Customer is Foreigner',];
  const currentMessages = useMemo(() => messagesMap[selectedUserId] || [], [messagesMap, selectedUserId]);

  const contextFromCustomer = useMemo(() => {
    if (!Array.isArray(chatMessagesFromCustomer)) return [];
    return chatMessagesFromCustomer.map(msg => ({
      role: 'user',
      content: msg.customer || msg.text || '',
    })).filter(m => m.content.trim() !== '');
  }, [chatMessagesFromCustomer]);

  useEffect(() => {
    if (!selectedUserId) return;
    const loadMessages = async () => {
      const ref = doc(db, 'gpt_chats', selectedUserId);
      const snap = await getDoc(ref);
      const data = snap.exists() ? snap.data().messages || [] : [];
      setMessagesMap(prev => ({ ...prev, [selectedUserId]: data }));
    };
    loadMessages();
  }, [selectedUserId]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }
    }, 100);

    return () => clearTimeout(timeout);
  }, [currentMessages]);

  const handleSubmit = async () => {
    const el = contentRef.current;
    if (!el || !selectedUserId) return;

    const imageTag = el.querySelector("img");
    const text = el.innerText.trim();

    if (!imageTag && !text) return;

    const cleanContent = text || (imageTag ? '' : '');
    const imageUrl = imageTag?.src || null;

    const newUserMsg = {
      role: 'curator',
      content: cleanContent,
      ...(imageUrl && { imageUrl })
    };

    const messagesBefore = messagesMap[selectedUserId] || [];
    const updated = [...messagesBefore, newUserMsg];
    setMessagesMap(prev => ({ ...prev, [selectedUserId]: updated }));
    el.innerHTML = '';
    setLoading(true);

    try {
      const fullContext = [...contextFromCustomer, ...updated];
      const res = await fetch('https://chatwithgpt-ivbcn7gbra-uc.a.run.app', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: selectedUserId,
          prompt: text || imageUrl || '',
          history: fullContext,
        }),
      });
      const data = await res.json();
      const reply = data.reply || 'ไม่สามารถตอบกลับได้ในขณะนี้';
      const full = [...updated, { role: 'assistant', content: reply }];
      setMessagesMap(prev => ({ ...prev, [selectedUserId]: full }));
      await setDoc(doc(db, 'gpt_chats', selectedUserId), { messages: full });
    } catch (err) {
      console.error('❌ GPT Error:', err);
    }

    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleImageUpload = async () => {
    if (!selectedUserId) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      try {
        const file = e.target.files?.[0];
        if (!file) return;

        const timestamp = Date.now();
        const fileName = file.name.replace(/[^\w.-]/g, '_');
        let path;
        try {
          path = `gpt_suggest_uploads/${selectedUserId}/${timestamp}_${fileName}`;
        } catch (err) {
          console.error("❌ Invalid path from selectedUserId:", err);
          alert("เกิดข้อผิดพลาดขณะสร้าง path สำหรับอัปโหลด กรุณาลองใหม่");
          return;
        }
        const refPath = storageRef(storage, path);
        await uploadBytes(refPath, file);
        const url = await getDownloadURL(refPath);

        if (contentRef.current) {
          const img = document.createElement('img');
          img.src = url;
          img.alt = 'upload';
          img.style.maxWidth = '100px';
          img.style.marginRight = '8px';
          img.style.borderRadius = '8px';
          img.style.verticalAlign = 'middle';
          img.dataset.imageUrl = url;
          contentRef.current.innerHTML = '';
          contentRef.current.appendChild(img);
          contentRef.current.appendChild(document.createTextNode(' '));
          contentRef.current.focus();
        }
      } catch (err) {
        console.error('🔥 Upload Error:', err);
      }
    };
    input.click();
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  useImperativeHandle(ref, () => ({
    askFromOutside(text) {
      if (!text || !selectedUserId) return;
      if (!contentRef.current) return;

      const match = text.match(/^ลูกค้าส่งภาพเข้ามาตามนี้:\s*(https:\/\/[^\s]+)/i);
      if (match) {
        const imageUrl = match[1];
        const img = document.createElement("img");
        img.src = imageUrl;
        img.alt = "อัปโหลดจากลูกค้า";
        img.style.maxWidth = "100px";
        img.style.marginRight = "8px";
        img.style.borderRadius = "8px";
        img.style.verticalAlign = "middle";

        contentRef.current.innerHTML = "";
        contentRef.current.appendChild(img);

        const outro = document.createElement("div");
        outro.innerText = "ลูกค้าส่งภาพนี้มา ช่วยดูภาพนี้แล้วแนะนำหน่อยว่าเราควรตอบกลับ หรือแนะนำอะไรเพิ่มเติมดี จากบริบทที่คุยกับลูกค้าทั้งหมด";
        outro.style.marginTop = "12px";
        outro.style.maxWidth = "100%";
        outro.style.whiteSpace = "pre-wrap";
        contentRef.current.appendChild(outro);

        const span = document.createElement("span");
        span.innerText = imageUrl;
        span.style.display = "none";
        contentRef.current.appendChild(span);
        contentRef.current.focus();
      } else {
        contentRef.current.innerText = text;
        contentRef.current.focus();
      }
    }
  }));

  return (
    <div className="w-full h-full min-h-0 overflow-y-auto px-6 py-6 bg-white border-t">
      <div className="max-w-3xl w-full mx-auto h-full flex flex-col font-normal text-[15px] leading-relaxed">
        <div className="text-base text-xl font-bold text-gray-700 pb-2 mb-4 border-b border-gray-200">
          Refinery GPT
        </div>

        <div ref={containerRef} className="flex-1 overflow-y-auto space-y-6 pb-4">
          {currentMessages.map((msg, idx) => {
            const isUser = msg.role === 'curator';
            const isAssistant = msg.role === 'assistant';
            const parts = isAssistant && typeof msg.content === 'string'
              ? msg.content.split('<<BREAK>>').map(p => p.trim()).filter(Boolean)
              : [msg.content];

            return (
              <div key={idx} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`relative px-4 py-3 rounded-xl shadow-sm whitespace-pre-line
            ${isUser ? 'bg-[#f7f7f8] text-gray-900' : 'bg-white text-gray-900 border border-gray-200'}
            max-w-[85%] w-fit`}
                  style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                >
                  {msg.imageUrl && (
                    <img
                      src={msg.imageUrl}
                      onClick={() => setPreviewImageUrl(msg.imageUrl)}
                      style={{
                        maxWidth: '100px',
                        borderRadius: '8px',
                        marginBottom: '8px',
                        display: 'block',
                        cursor: 'pointer'
                      }}
                      alt="uploaded"
                    />
                  )}

                  {msg.role === 'assistant' && (() => {
                    const raw = typeof msg.content === 'string' ? msg.content : '';
                    // NOTE: ให้ extractImageUrls คืน "ทั้ง match" รวม ?alt=media
                    const urlsInText = extractImageUrls(raw);

                    // โหมดใหม่ (URL ตรง)
                    if (urlsInText.length > 0) {
                      const unique = Array.from(new Set(urlsInText)).slice(0, 12); // กันซ้ำ + limit
                      return (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {unique.map((url, index) => (
                            <FitThumb key={index} url={url} onClick={setPreviewImageUrl} />
                          ))}
                        </div>
                      );
                    }

                    // Fallback เดิม (รองรับ alias เก่า ๆ)
                    const imageUrls = typeof resolveAllImageUrlsFromAlias === 'function'
                      ? resolveAllImageUrlsFromAlias(raw)
                      : [];
                    if (imageUrls.length === 0) return null;

                    return (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {imageUrls.map((url, index) => (
                          <FitThumb key={index} url={url} onClick={setPreviewImageUrl} frame="4/5" width={120} />
                        ))}
                      </div>
                    );
                  })()}

                  {parts.map((part, i) => {
                    const rawPart = typeof part === 'string' ? part : '';

                    // 1) ตัด URL รูปออก → 2) ตัดหัวบล็อกภาพ → 3) ล้าง alias ในข้อความ
                    const textNoUrls = stripImageUrlsFromText(rawPart);
                    const textNoHeaders = stripImageSetHeaders(textNoUrls);
                    const html = isUser ? rawPart : cleanAliasFromText(textNoHeaders);

                    // ข้ามบล็อกว่างหลัง clean แล้ว
                    if (!html || !String(html).trim()) return null;

                    return (
                      <div key={i} className="mb-3">
                        <div dangerouslySetInnerHTML={{ __html: html }} />
                        {i < parts.length - 1 && (
                          <hr className="mt-7 mb-5 border-t border-gray-300 w-full max-w-[100%] mx-auto" />
                        )}
                      </div>
                    );
                  })}

                  {!isUser && (
                    <div className="flex justify-end mt-2">
                      <button
                        onClick={() =>
                          handleCopy(
                            // คัดลอกเฉพาะข้อความ: ตัด URL รูป + หัวบล็อกภาพ + ล้าง alias
                            cleanAliasFromText(
                              stripImageSetHeaders(
                                stripImageUrlsFromText(
                                  typeof msg.content === 'string' ? msg.content : ''
                                )
                              )
                            )
                          )
                        }
                        className="text-gray-400 hover:text-gray-600 text-xs flex items-center gap-1"
                      >
                        <Copy className="w-4 h-4" />
                        Copy
                      </button>
                    </div>
                  )}

                </div>
              </div>
            );
          })}
        </div>

        {previewImageUrl && (
          <div
            onClick={() => setPreviewImageUrl(null)}
            className="fixed inset-0 z-50 bg-black bg-opacity-70 flex items-center justify-center"
          >
            <div
              className="bg-white rounded-lg p-4 shadow-xl relative"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={previewImageUrl}
                alt="Full Preview"
                className="max-w-[75vw] max-h-[70vh] rounded mb-4"
              />
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setPreviewImageUrl(null)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition duration-200 shadow-sm hover:shadow-md"
                >
                  Close
                </button>
                <button
                  onClick={() => handleSendImageToLine(previewImageUrl)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-black text-white text-sm font-medium hover:bg-gray-800 transition duration-200 shadow-sm hover:shadow-md"
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-auto border-t pt-4 relative">
          <div className="w-full bg-gray-100 rounded-3xl flex items-end px-2 py-1 relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="w-8 h-8 bg-transparent rounded-full flex items-center justify-center"
              title="More options"
            >
              <Plus className="w-4 h-4 text-gray-700" />
            </button>

            <div
              ref={contentRef}
              contentEditable
              onKeyDown={handleKeyDown}
              className="flex-1 bg-transparent text-sm px-3 py-2 focus:outline-none min-h-[38px]"
              style={{ lineHeight: '1.5', overflow: 'auto', maxHeight: '200px' }}
              placeholder="Ask refinery GPT"
            />

            <button
              onClick={handleSubmit}
              disabled={loading || !selectedUserId}
              className="w-8 h-8 bg-black hover:bg-gray-800 rounded-full flex items-center justify-center disabled:opacity-50"
            >
              <ArrowUp className="w-4 h-4 text-white" />
            </button>

            {showMenu && (
              <div className="absolute bottom-14 left-0 w-64 bg-white border rounded-xl shadow-xl p-3 z-50">
                <div className="space-y-2 text-sm text-gray-800">
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      handleImageUpload();
                    }}
                    className="w-full flex items-center gap-2 px-2 py-2 rounded hover:bg-gray-100"
                  >
                    <Paperclip className="w-4 h-4 text-gray-600" />
                    <span>Add photos</span>
                  </button>

                  <div className="border-t pt-2 mt-2">
                    <div className="text-gray-500 text-xs px-2 pb-1">Template Prompt</div>
                    {templateOptions.map((template, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          if (contentRef.current) {
                            contentRef.current.innerText = template;
                          }
                          setShowMenu(false);
                        }}
                        className="w-full text-left hover:bg-gray-100 px-2 py-1 rounded flex items-center gap-2"
                      >
                        <Sparkles className="w-4 h-4 text-gray-600" />
                        {template}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {successMessage && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-white shadow-xl rounded-xl px-6 py-4 border border-gray-200 text-center max-w-sm w-full">
            <div className="text-sm text-gray-800 mb-3 whitespace-pre-line">{successMessage}</div>
            <button
              onClick={() => setSuccessMessage('')}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition duration-200 shadow-sm hover:shadow-md"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

export default GptSuggest;
